import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Training } from '@/types/trainings';
import { useToast } from '@/hooks/use-toast';

interface TrainingInstructor {
  id: string;
  instructor_user_id?: string;
  name?: string;
  username?: string;
  profile_picture_url?: string;
  belt_rank?: string;
  manual_name?: string;
  is_manual: boolean;
  created_at: string;
}

interface UseTrainingInstructorProps {
  training?: Training | null;
  trainingId?: string;
  refreshKey?: number;
}

export const useTrainingInstructor = (props: UseTrainingInstructorProps | null) => {
  const { training, trainingId, refreshKey = 0 } = props || {};
  const { user } = useAuth();
  const { toast } = useToast();
  const [instructor, setInstructor] = useState<TrainingInstructor | null>(null);
  const [loading, setLoading] = useState(false);

  // Get the actual training ID
  const actualTrainingId = training?.id || trainingId;

  // Load instructor when training changes or refresh is triggered
  useEffect(() => {
    const fetchInstructor = async () => {
      if (!actualTrainingId || !user) {
        return;
      }
      
      setLoading(true);
      try {
        const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
        const token = session?.access_token;

        // Use local Supabase functions URL in development
        const baseUrl = import.meta.env.DEV
          ? 'http://127.0.0.1:54321'
          : import.meta.env.VITE_SUPABASE_URL;

        const response = await fetch(`${baseUrl}/functions/v1/get_training_instructor`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            training_id: actualTrainingId,
            _t: Date.now() + refreshKey + Math.random() // Enhanced cache busting
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('🔄 useTrainingInstructor - Response error:', errorText);
          throw new Error('Failed to fetch instructor');
        }
        
        const result = await response.json();
        
        if (result.instructor) {
          setInstructor(result.instructor);
        } else {
          setInstructor(null);
        }
      } catch (error) {
        console.error('🔄 useTrainingInstructor - Error fetching instructor:', error);
        setInstructor(null);
      } finally {
        setLoading(false);
      }
    };

    fetchInstructor();
  }, [actualTrainingId, user, refreshKey]);

  // Clear instructor
  const clearInstructor = useCallback(() => {
    setInstructor(null);
  }, []);

  // Force reload instructor
  const reloadInstructor = useCallback(async () => {
    if (!actualTrainingId || !user) return;
    
    setLoading(true);
    try {
      const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
      const token = session?.access_token;

      const baseUrl = import.meta.env.DEV 
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${baseUrl}/functions/v1/get_training_instructor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          training_id: actualTrainingId,
          _t: Date.now() + Math.random() // Enhanced cache busting
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.instructor) {
          setInstructor(result.instructor);
        } else {
          setInstructor(null);
        }
      }
    } catch (error) {
      console.error('🔄 useTrainingInstructor - Error force reloading instructor:', error);
      setInstructor(null);
    } finally {
      setLoading(false);
    }
  }, [actualTrainingId, user]);

  return {
    instructor,
    loading,
    clearInstructor,
    reloadInstructor,
  };
}; 